'use client';

import clsx from 'clsx';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import { Autoplay, Mousewheel, Navigation, Pagination } from 'swiper/modules';
import { Swiper, type SwiperProps } from 'swiper/react';
import 'swiper/swiper-bundle.css';

import { getSpacingValue } from '@commons/helpers/styles';

import { CarouselControls, CarouselControlsLoading } from './Controls';
import CarouselSlide from './Slide';
import styles from './index.module.scss';

/**
 * Default autoplay options.
 *
 * @see https://swiperjs.com/swiper-api#autoplay
 */

const autoplayDefaultOptions = {
  delay: 5000,
  pauseOnMouseEnter: true,
  disableOnInteraction: true,
};

/**
 * Default navigation options.
 *
 * @see https://swiperjs.com/swiper-api#navigation
 */

const navigationDefaultOptions = {
  nextEl: `.${styles.next}`,
  prevEl: `.${styles.prev}`,
  disabledClass: styles.disabled,
};

/**
 * Default pagination options.
 *
 * @see https://swiperjs.com/swiper-api#pagination
 */

const paginationDefaultOptions = {
  el: `.${styles.pagination}`,
  clickable: true,
  bulletClass: styles.bullet,
  bulletActiveClass: styles.active,
};

/**
 * Carousel component.
 *
 * @see https://swiperjs.com/swiper-api
 */

export const Carousel = (props: SwiperProps) => {
  const {
    children,
    autoplay = false,
    navigation = true,
    pagination = false,
    breakpoints,
    spaceBetween = getSpacingValue('md'),
    className,
    ...rest
  } = props;

  const autoplayOption = autoplay === true ? autoplayDefaultOptions : autoplay;
  const navigationOptions = navigation === true ? navigationDefaultOptions : navigation;
  const paginationOptions = pagination === true ? paginationDefaultOptions : pagination;

  const classes = clsx(styles.carousel, 'carousel', className);

  return (
    <Swiper
      modules={[Pagination, Navigation, Autoplay, Mousewheel]}
      mousewheel={{
        forceToAxis: true, // Enable axis-specific scrolling
        releaseOnEdges: true,
      }}
      autoplay={autoplayOption}
      navigation={navigationOptions}
      pagination={paginationOptions}
      breakpoints={breakpoints}
      spaceBetween={spaceBetween}
      className={classes}
      {...rest}
    >
      {children}
    </Swiper>
  );
};

/**
 * Carousel Loading component.
 *
 * The HTML returned by Swiper during SSR causes layout shifts, causing issues with other features like anchor links.
 * This component can be used to render an initial loading state that matches the final layout.
 *
 * If controls are needed, the ControlsLoading component should be used alongside this component.
 */

export const CarouselLoading = ({ children, className }: SwiperProps) => {
  const classes = clsx(styles.carousel, 'carousel', styles.loading, className);

  return (
    <div className={classes}>
      <div className="swiper swiper-initialized swiper-horizontal">
        <div className="swiper-wrapper">{children}</div>
      </div>
    </div>
  );
};

// Adding components to the Carousel namespace.

Carousel.Loading = CarouselLoading;

Carousel.Slide = CarouselSlide;

Carousel.Controls = CarouselControls;

Carousel.ControlsLoading = CarouselControlsLoading;

export default Carousel;
